import { IconPencil } from '@tabler/icons-react';
import type { EnvironmentVariable } from '@xver/apis/v1/kufer/containers/model';
import { Button } from '@xver/components/Atoms';
import DialogBox from '@xver/components/Atoms/DialogBox/DialogBox';
import Editor from '@xver/components/Atoms/Editor/Editor';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { STEP_CONFIGURATION, STEP_SOURCE } from '../utils/validation';
import type { ContainerConfigFormValue, EntrypointValue } from './ContainerConfigForm';
import type { GitHubSourceFormValue } from './GitHubSourceForm';
import type { InlineSourceFormValue } from './InlineSourceForm';
import type { SourceType } from './SourceTypeSelector';
import type { UrlSourceFormValue } from './UrlSourceForm';

interface ReviewStepProps {
    sourceType: SourceType;
    urlSource: UrlSourceFormValue;
    inlineSource: InlineSourceFormValue;
    githubSource: GitHubSourceFormValue;
    config: ContainerConfigFormValue;
    entrypoint: EntrypointValue;
    environmentVariables: EnvironmentVariable[];
    onEditStep: (step: number) => void;
}

function ReviewRow({ label, value }: { label: string; value: ReactNode }) {
    return (
        <div className="w-full flex flex-row justify-between items-start gap-4 text-sm">
            <dt className="text-fg/68">{label}</dt>
            <dd className="font-mono text-fg text-right break-all">{value}</dd>
        </div>
    );
}

export default function ReviewStep({
    sourceType,
    urlSource,
    inlineSource,
    githubSource,
    config,
    entrypoint,
    environmentVariables,
    onEditStep,
}: ReviewStepProps) {
    const { t } = useTranslation('containerEngine');

    const none = t('deployment.review.none', '—');
    const sourceTitle = t('deployment.review.sourceTitle', 'Source');
    const configTitle = t('deployment.review.configTitle', 'Runtime configuration');
    const envTitle = t('deployment.review.envTitle', 'Environment variables');

    const editButton = (step: number, section: string) => (
        <Button
            size="sm"
            variant="secondary"
            leftIcon={<IconPencil size={14} />}
            onClick={() => onEditStep(step)}
            aria-label={t('deployment.review.editAria', {
                section,
                defaultValue: 'Edit {{section}}',
            })}
        >
            {t('deployment.review.edit', 'Edit')}
        </Button>
    );

    const list = (values: string[]) => (values.length > 0 ? values.join(' ') : none);

    return (
        <div className="w-full flex flex-col gap-4">
            <DialogBox
                label={sourceTitle}
                description={t(
                    'deployment.review.sourceDescription',
                    'Where the image comes from.'
                )}
                rightDescription={editButton(STEP_SOURCE, sourceTitle)}
            >
                {sourceType === 'url' && (
                    <dl className="w-full flex flex-col gap-2">
                        <ReviewRow
                            label={t('deployment.review.image', 'Image')}
                            value={urlSource.image || none}
                        />
                        <ReviewRow
                            label={t('deployment.review.imageTag', 'Tag')}
                            value={urlSource.image_tag || none}
                        />
                        <ReviewRow
                            label={t('deployment.review.credential', 'Registry credential')}
                            value={
                                urlSource.registry_credential_id ||
                                t('deployment.review.credentialNone', 'None')
                            }
                        />
                        <ReviewRow
                            label={t('deployment.review.pullPolicy', 'Image pull policy')}
                            value={urlSource.image_pull_policy}
                        />
                        <ReviewRow
                            label={t('deployment.review.autoUpdate', 'Auto-update')}
                            value={
                                urlSource.enable_auto_update
                                    ? t('deployment.review.enabled', 'Enabled')
                                    : t('deployment.review.disabled', 'Disabled')
                            }
                        />
                        {urlSource.enable_auto_update && (
                            <ReviewRow
                                label={t('deployment.review.mutableTags', 'Mutable tags')}
                                value={list(urlSource.mutable_tags)}
                            />
                        )}
                    </dl>
                )}

                {sourceType === 'inline' && (
                    <div className="w-full flex flex-col gap-2">
                        <span className="text-sm text-fg/68">
                            {t('deployment.review.dockerfile', 'Dockerfile')}
                        </span>
                        <Editor
                            readOnly
                            enableCopy
                            filename="Dockerfile"
                            height={220}
                            initialValue={inlineSource.inline}
                            textareaProps={{
                                'aria-label': t(
                                    'deployment.inlineSource.editorAria',
                                    'Dockerfile contents'
                                ),
                            }}
                        />
                    </div>
                )}

                {sourceType === 'github' && (
                    <dl className="w-full flex flex-col gap-2">
                        <ReviewRow
                            label={t('deployment.review.repository', 'Repository')}
                            value={
                                githubSource.github_owner && githubSource.github_repository
                                    ? `${githubSource.github_owner}/${githubSource.github_repository}`
                                    : none
                            }
                        />
                        <ReviewRow
                            label={t('deployment.review.branch', 'Branch')}
                            value={githubSource.github_branch || none}
                        />
                        <ReviewRow
                            label={t('deployment.review.dockerfilePath', 'Dockerfile path')}
                            value={githubSource.dockerfile_path || 'Dockerfile'}
                        />
                    </dl>
                )}
            </DialogBox>

            <DialogBox
                label={configTitle}
                description={t(
                    'deployment.review.configDescription',
                    'Networking, request handling, and entrypoint.'
                )}
                rightDescription={editButton(STEP_CONFIGURATION, configTitle)}
            >
                <div className="w-full flex flex-col gap-3">
                    <dl className="w-full flex flex-col gap-2">
                        <ReviewRow
                            label={t('deployment.review.port', 'Container port')}
                            value={config.container_port}
                        />
                        <ReviewRow
                            label={t('deployment.review.timeout', 'Request timeout')}
                            value={t('deployment.review.seconds', {
                                value: config.request_timeout,
                                defaultValue: '{{value}} s',
                            })}
                        />
                        <ReviewRow
                            label={t('deployment.review.concurrency', 'Request concurrency')}
                            value={config.request_concurrency}
                        />
                        <ReviewRow
                            label={t('deployment.review.entrypointCommand', 'Entrypoint command')}
                            value={list(entrypoint.entrypoint_command)}
                        />
                        <ReviewRow
                            label={t(
                                'deployment.review.entrypointArgument',
                                'Entrypoint arguments'
                            )}
                            value={list(entrypoint.entrypoint_argument)}
                        />
                    </dl>
                    <p className="text-xs text-fg/68">
                        {t(
                            'deployment.review.seeSummaryPanel',
                            'CPU, memory, replicas and the cost estimate are in the summary panel.'
                        )}
                    </p>
                </div>
            </DialogBox>

            <DialogBox
                label={envTitle}
                description={t(
                    'deployment.review.envDescription',
                    'Injected into the container at runtime.'
                )}
                rightDescription={editButton(STEP_CONFIGURATION, envTitle)}
            >
                {environmentVariables.length === 0 ? (
                    <p className="text-sm text-fg/68">
                        {t(
                            'deployment.envVars.empty',
                            'No environment variables set. Add one to override defaults at runtime.'
                        )}
                    </p>
                ) : (
                    <dl className="w-full flex flex-col gap-2">
                        {environmentVariables.map((variable) => (
                            <ReviewRow
                                key={variable.name}
                                label={variable.name}
                                value={variable.value || none}
                            />
                        ))}
                    </dl>
                )}
            </DialogBox>
        </div>
    );
}
